/* OWN — Stoffeya product gallery upgrade (Option A + PhotoSwipe)
 *
 * Builds on Trade's own <media-gallery> / <slider-component> instead of replacing them:
 *   - rewind-style loop on the prev/next arrows (same idea as Trade's SlideshowComponent)
 *   - ←/→ keyboard navigation
 *   - mouse drag on desktop (touch already swipes natively)
 *   - pagination dots, kept in sync with the scroll position
 *   - click-to-enlarge opens PhotoSwipe (full screen, swipe, pinch/double-tap zoom)
 *     instead of Trade's product-media-modal. PhotoSwipe is loaded on the first click only.
 *
 * Loaded by sections/main-product.liquid with data-pswp / data-pswp-css asset URLs.
 * Variant switching, video, 3D and the thumbnail strip keep using Trade's untouched code.
 */
(() => {
  const script = document.currentScript;
  const config = {
    pswp: script?.dataset.pswp,
    pswpCss: script?.dataset.pswpCss,
  };
  const isGerman = (document.documentElement.lang || '').toLowerCase().startsWith('de');
  const strings = isGerman
    ? {
        close: 'Schließen',
        zoom: 'Zoomen',
        prev: 'Vorheriges Bild',
        next: 'Nächstes Bild',
        error: 'Das Bild kann nicht geladen werden',
      }
    : {
        close: 'Close',
        zoom: 'Zoom',
        prev: 'Previous image',
        next: 'Next image',
        error: 'The image cannot be loaded',
      };

  let photoSwipePromise = null;
  const loadPhotoSwipe = () => {
    if (photoSwipePromise) return photoSwipePromise;
    if (config.pswpCss && !document.querySelector(`link[href="${config.pswpCss}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = config.pswpCss;
      document.head.appendChild(link);
    }
    photoSwipePromise = import(config.pswp).then((m) => m.default);
    return photoSwipePromise;
  };

  const largestSource = (img) => {
    const candidates = (img.getAttribute('srcset') || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => {
        const [url, descriptor] = s.split(/\s+/);
        return { url, w: parseInt(descriptor, 10) || 0 };
      })
      .sort((a, b) => b.w - a.w);
    return candidates[0] || { url: img.currentSrc || img.src, w: img.naturalWidth || 1600 };
  };

  const initGallery = (gallery) => {
    if (gallery.dataset.stoffeyaGallery || gallery.closest('quick-add-modal')) return;
    gallery.dataset.stoffeyaGallery = 'ready';
    gallery.classList.add('stoffeya-gallery');

    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const list = viewer && viewer.querySelector('[id^="Slider-Gallery"]');
    if (!viewer || !list) return;

    const controls = viewer.querySelector('.slider-buttons');
    const prevButton = viewer.querySelector('button[name="previous"]');
    const nextButton = viewer.querySelector('button[name="next"]');

    const slides = () => Array.from(list.querySelectorAll('.product__media-item')).filter((li) => li.clientWidth > 0);
    const slideOffset = () => {
      const items = slides();
      return items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : list.clientWidth || 1;
    };
    const currentIndex = () => {
      const items = slides();
      if (items.length < 2) return 0;
      return Math.max(0, Math.min(items.length - 1, Math.round(list.scrollLeft / slideOffset())));
    };
    const scrollToIndex = (i) => {
      const items = slides();
      if (!items.length) return;
      const n = items.length;
      const target = items[((i % n) + n) % n];
      list.scrollTo({ left: target.offsetLeft });
    };

    /* ---- loop: Trade disables the arrows at either end; wrap around instead ---- */
    const enableLooping = () => {
      viewer.enableSliderLooping = true;
      prevButton && prevButton.removeAttribute('disabled');
      nextButton && nextButton.removeAttribute('disabled');
    };
    enableLooping();
    if (window.customElements) customElements.whenDefined('slider-component').then(enableLooping);

    if (controls) {
      controls.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest('button[name="previous"], button[name="next"]');
          if (!button) return;
          const items = slides();
          if (items.length < 2) return;
          const index = currentIndex();
          const goingNext = button.name === 'next';
          if ((goingNext && index >= items.length - 1) || (!goingNext && index <= 0)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            scrollToIndex(goingNext ? 0 : items.length - 1);
          }
        },
        true
      );
    }

    /* ---- dots ---- */
    const dots = document.createElement('div');
    dots.className = 'stoffeya-dots';
    dots.setAttribute('aria-hidden', 'true');
    viewer.appendChild(dots);

    const renderDots = () => {
      const count = slides().length;
      gallery.classList.toggle('stoffeya-gallery--single', count < 2);
      dots.innerHTML = count > 1 ? Array.from({ length: count }, () => '<i></i>').join('') : '';
    };
    const sync = () => {
      const index = currentIndex();
      Array.from(dots.children).forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      prevButton && prevButton.removeAttribute('disabled');
      nextButton && nextButton.removeAttribute('disabled');
    };
    // Trade measures slide offsets once in its constructor; if that ran before the stylesheets
    // finished loading (only one slide visible) the counter shows "NaN". Re-measure to be safe.
    const refreshPages = () => {
      if (typeof viewer.resetPages === 'function') viewer.resetPages();
      sync();
    };
    renderDots();
    refreshPages();
    window.addEventListener('load', refreshPages);
    if (window.customElements) customElements.whenDefined('slider-component').then(refreshPages);

    list.addEventListener('scroll', sync, { passive: true });
    // variant changes re-order / re-render the slides
    new MutationObserver(() => {
      renderDots();
      refreshPages();
    }).observe(list, { childList: true });
    window.addEventListener('resize', sync);

    /* ---- keyboard ---- */
    gallery.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target.closest('input, textarea, select')) return;
      event.preventDefault();
      scrollToIndex(currentIndex() + (event.key === 'ArrowRight' ? 1 : -1));
    });

    /* ---- mouse drag on desktop ---- */
    // While dragging, scroll-snap is switched off (.is-dragging). On release we animate to the
    // target slide ourselves and only re-enable snapping once the animation has finished —
    // re-enabling it earlier makes the browser snap instantly, which reads as a "jump".
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let settleFrame = 0;
    const settleTo = (left, done) => {
      cancelAnimationFrame(settleFrame);
      const from = list.scrollLeft;
      const distance = left - from;
      if (!distance || reducedMotion.matches) {
        list.scrollLeft = left;
        done && done();
        return;
      }
      const duration = Math.min(420, 220 + Math.abs(distance) * 0.25);
      const t0 = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        list.scrollLeft = from + distance * eased;
        if (p < 1) settleFrame = requestAnimationFrame(step);
        else {
          list.scrollLeft = left;
          done && done();
        }
      };
      settleFrame = requestAnimationFrame(step);
    };

    let dragging = false;
    let dragMoved = false;
    let dragStartX = 0;
    let dragStartLeft = 0;
    let dragStartTime = 0;
    let dragPointer = null;
    let suppressClick = false;

    list.addEventListener('dragstart', (event) => event.preventDefault());
    list.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      cancelAnimationFrame(settleFrame);
      dragging = true;
      dragMoved = false;
      dragStartX = event.clientX;
      dragStartLeft = list.scrollLeft;
      dragStartTime = performance.now();
      dragPointer = event.pointerId;
    });
    list.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - dragStartX;
      if (!dragMoved && Math.abs(dx) > 6) {
        dragMoved = true;
        list.classList.add('is-dragging');
        try {
          list.setPointerCapture(dragPointer);
        } catch (e) {
          /* ignore */
        }
      }
      if (dragMoved) {
        event.preventDefault();
        list.scrollLeft = dragStartLeft - dx;
      }
    });
    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      if (!dragMoved) return;
      const items = slides();
      if (items.length > 1) {
        const offset = slideOffset();
        const moved = list.scrollLeft - dragStartLeft; // + = towards the next slide
        const elapsed = performance.now() - dragStartTime;
        const flick = Math.abs(moved) > 12 && elapsed < 300; // short, quick swipe
        const farEnough = Math.abs(moved) > offset * 0.15;
        const start = Math.round(dragStartLeft / offset);
        const step = flick || farEnough ? Math.sign(moved) : 0;
        const target = Math.max(0, Math.min(items.length - 1, start + step));
        settleTo(items[target].offsetLeft, () => list.classList.remove('is-dragging'));
      } else {
        list.classList.remove('is-dragging');
      }
      suppressClick = true;
      setTimeout(() => (suppressClick = false), 50);
    };
    list.addEventListener('pointerup', endDrag);
    list.addEventListener('pointercancel', endDrag);

    /* ---- click to enlarge → PhotoSwipe ---- */
    const openLightbox = async (clickedButton) => {
      const entries = slides()
        .map((li) => {
          const img = li.querySelector('.product__modal-opener--image img');
          if (!img) return null;
          const best = largestSource(img);
          const w = parseInt(img.getAttribute('width'), 10) || img.naturalWidth || best.w;
          const h = parseInt(img.getAttribute('height'), 10) || img.naturalHeight || w;
          return {
            src: best.url,
            width: best.w,
            height: Math.round((best.w * h) / w),
            alt: img.alt || '',
            msrc: img.currentSrc || img.src,
            element: img,
            slide: li,
          };
        })
        .filter(Boolean);
      if (!entries.length) return;

      const clickedSlide = clickedButton.closest('.product__media-item');
      const index = Math.max(
        0,
        entries.findIndex((entry) => entry.slide === clickedSlide)
      );

      const PhotoSwipe = await loadPhotoSwipe();
      const pswp = new PhotoSwipe({
        dataSource: entries,
        index,
        bgOpacity: 0.96,
        showHideAnimationType: 'zoom',
        initialZoomLevel: 'fit',
        secondaryZoomLevel: 2,
        maxZoomLevel: 4,
        wheelToZoom: true,
        padding: { top: 20, bottom: 20, left: 12, right: 12 },
        closeTitle: strings.close,
        zoomTitle: strings.zoom,
        arrowPrevTitle: strings.prev,
        arrowNextTitle: strings.next,
        errorMsg: strings.error,
        indexIndicatorSep: ' / ',
      });
      // when the viewer closes, leave the inline slider on the photo the shopper was looking at
      pswp.on('close', () => {
        const entry = entries[pswp.currIndex];
        if (entry && entry.slide) list.scrollTo({ left: entry.slide.offsetLeft });
      });
      pswp.init();
    };

    gallery.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('.product__modal-opener--image .product__media-toggle');
        if (!button || !gallery.contains(button)) return;
        event.preventDefault();
        event.stopPropagation();
        if (suppressClick || !config.pswp) return;
        openLightbox(button);
      },
      true
    );
  };

  const initAll = () => document.querySelectorAll('media-gallery').forEach(initGallery);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
